import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { SWRResponse } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { CompanyBase, CompanyProfile } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { CompanyProfileService } from 'services/CompanyProfileService';

import { prepareConditionalSwrArgs } from './prepare-conditional-swr-args';

const useCompanyProfile = (
    profile?: CompanyBase | CompanyBase['id'] | null,
): SWRResponse & {
    company: CompanyProfile | undefined;
    refetch: () => Promise<CompanyProfile | undefined>;
} => {
    const profileId = CollectionHelpers.id(profile);

    const swr = useSWRImmutable(
        ...prepareConditionalSwrArgs({
            key: CompanyHelpers.swr.profile(profileId!),
            fetcher: () => CompanyProfileService.get(profileId!),
            condition: Boolean(profileId),
        }),
        { fallbackData: typeof profile === 'object' ? profile : undefined },
    );

    const refetch = async () => {
        if (!profileId) return undefined;
        return swr.mutate(undefined, { revalidate: true });
    };

    return {
        ...swr,
        company: swr?.data,
        refetch,
    };
};

const useCompanyProfileBySlug = (
    slug?: CompanyProfile['slug'] | null,
): SWRResponse & {
    company: CompanyProfile | undefined;
} => {
    const swr = useSWRImmutable(
        ...prepareConditionalSwrArgs({
            key: CompanyHelpers.swr.profile(slug!),
            fetcher: () => CompanyProfileService.getBySlug(slug!),
            condition: Boolean(slug),
        }),
    );

    return {
        ...swr,
        company: swr.data ? (swr.data as CompanyProfile) : undefined,
    };
};

export { useCompanyProfile, useCompanyProfileBySlug };
